import Vue from 'vue';
import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/auth';
import 'firebase/functions';
import VueMaterial from 'vue-material';

import VueResource from 'vue-resource';
import Notifications from 'vue-notification';
import Toast from 'vue-toastification';
import 'vue-material/dist/vue-material.css';
import 'vue-toastification/dist/index.css';
import App from './App';
import router from './router';
import store from './lib/vuex/Store';
import { installApiAuth } from './lib/api-auth-client';
import { installErrorBuffer } from './lib/errorBuffer';
import { watchSession, sessionDeleteAllowed } from './lib/session-watch';
import { lastPageName } from './lib/page-names';
import './styles/extra.scss';
import './styles/design-system.scss';

// Capture recent console/window errors so early-adopter bug reports carry
// context. Harmless + tiny for everyone; only read when a report is filed.
installErrorBuffer();

const toastOptions = {
    position: 'top-right',
    timeout: 3000,
    closeOnClick: true,
    pauseOnFocusLoss: true,
    pauseOnHover: true,
    draggable: true,
    draggablePercent: 0.6,
    closeButton: 'button',
    icon: true
};

window.objectPath = require('object-path');

const config = {
    apiKey: `${process.env.VUE_APP_FIREBASE_APP_KEY}`,
    authDomain: `${process.env.VUE_APP_FIREBASE_AUTHDOMAIN}`,
    databaseURL: `${process.env.VUE_APP_FIREBASE_DATABASE_URL}`,
    projectId: `${process.env.VUE_APP_FIREBASE_PROJECTID}`,
    storageBucket: `${process.env.VUE_APP_FIREBASE_STORAGE_BUCKET}`,
    messagingSenderId: `${process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID}`,
    appId: `${process.env.VUE_APP_FIREBASE_APP_ID}`,
    measurementId: `${process.env.VUE_APP_FIREBASE_MEASUREMENT_ID}`
};
firebase.initializeApp(config);

// eslint-disable-next-line import/prefer-default-export
export const db = firebase.firestore();

const settings = { timestampsInSnapshots: true};
db.settings(settings);

(async () => {
    try {
        await db.enablePersistence({ experimentalTabSynchronization: true });
    } catch (err) {
        if (err.code === 'failed-precondition') {
            // Multiple tabs open, persistence can only be enabled in one tab at a time
        } else if (err.code === 'unimplemented') {
            // The current browser does not support all features required to enable persistence
        }
    }
})();

Vue.use(VueResource);
// Every vue-resource request to the Cuelist backend carries the user's ID token.
installApiAuth(Vue);
Vue.use(Notifications);
Vue.use(Toast, toastOptions);

Vue.config.productionTip = false;

// "/ 84" after a page box. Old versions can have no page list (21 Oct 2026): blank, not a crash.
Vue.filter('lastPageName', pages => lastPageName(pages));

Vue.use(VueMaterial);
Vue.material.registerTheme({
    default: {
        primary: {
            color: '#0d2744',
            hue: 800
        }
    },
    'show-mode': {
        background: {
            color: 'grey',
            hue: 800
        },
        textColor: 'white'
    },
    white: {
        textColor: 'black',
        background: 'white'
    }
});

async function handleLogout(userId, sessionId) {
    const preservedValue = localStorage.getItem('age_certify_policy_accepted');
    localStorage.clear();

    if (preservedValue !== null) {
        localStorage.setItem('age_certify_policy_accepted', preservedValue);
    }

    // Never written signed out or for an empty id: that was the stray `userDeviceSession.null`
    // write (lib/session-watch.js).
    if (sessionDeleteAllowed(firebase.auth().currentUser, userId, sessionId)) {
        try {
            await db.collection('users').doc(userId).update({
                [`userDeviceSession.${sessionId}`]: firebase.firestore.FieldValue.delete()
            });
        } catch (error) {
            console.error('Could not remove the device session:', error);
        }
    }

    router.push('/login');
}

// One Vue app, and one session watch per signed-in user, stopped on every sign-in change.
let vueApp = null;
let stopSessionWatch = () => {};
firebase.auth().onAuthStateChanged((user) => {
    stopSessionWatch();
    stopSessionWatch = () => {};
    if (user) {
        stopSessionWatch = watchSession(user.uid, {
            storage: localStorage,
            onUserDoc: (uid, cb) => db.collection('users').doc(uid).onSnapshot(cb),
            setTimeout: (fn, ms) => setTimeout(fn, ms),
            clearTimeout: t => clearTimeout(t),
            setInterval: (fn, ms) => setInterval(fn, ms),
            clearInterval: i => clearInterval(i),
            logout: handleLogout
        });
    }

    if (!vueApp) {
        vueApp = new Vue({
            el: '#app',
            store,
            router,
            template: '<App/>',
            components: { App }
        });
    }
});

const isIE = () => {
    const ua = navigator.userAgent;
    return ua.includes('MSIE ') || ua.includes('Trident/');
};
if (isIE()) {
    // eslint-disable-next-line no-alert
    alert('Cuelist does not function properly on Internet Explorer. Please load your projects on another browser to properly utilize the software. Cuelist works best on Google Chrome, Apple Safari and Mozilla Firefox. We apologize for any inconvenience.');
}

window.appendDynamicStylesheet = function(stylesText) {
    const styleNode = document.createElement('style');
    styleNode.type = 'text/css';
    if (styleNode.styleSheet) styleNode.styleSheet.cssText = stylesText;
    else styleNode.appendChild(document.createTextNode(stylesText));
    document.querySelector('body').append(styleNode);
};

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
if (isSafari) {
    const stylesText = '.ql-project-list-view-table {border-collapse: separate !important;}';
    window.appendDynamicStylesheet(stylesText);
}

// eslint-disable-next-line no-extend-native
String.prototype.toCamelCase = function() {
    const capitalize = str => str.charAt(0).toUpperCase() + str.toLowerCase().slice(1);
    const string = this.toLowerCase().replaceAll(/[^A-Za-z0-9]/g, ' ').split(' ').reduce((result, word) => result + capitalize(word.toLowerCase()), '');
    return string.charAt(0).toLowerCase() + string.slice(1);
};

// Stage 0 (dev-only): cue-geometry golden-master capture. Code-split behind a
// query flag so it is never loaded in normal use. See src/lib/stage0/.
if (/[?&]geomcapture=1\b/.test(window.location.search)) {
    import('./lib/stage0/geomCapture').then(m => m.installGeometryCapture());
}

// Stage 0 (dev-only): annotation-layer performance HUD (FPS + interaction
// latency). Requires a project open in the editor. See src/lib/stage0/.
if (/[?&]perfhud=1\b/.test(window.location.search)) {
    import('./lib/stage0/perfHud').then(m => m.installPerfHud());
}


// WEBPACK FOOTER //
// ./src/main.js